<template>
  <div class="pan-resize-control-group" v-if="activeItem?.geometry && dragAndResize">
    <div class="pan-resize-control-group__inner">
      <div :class="{ button: true, 'button--active button--disabled': !resize }" @click="startResize">
        <Hand32Svg />
      </div>
      <div :class="{ button: true, 'button--active button--disabled': resize }" @click="startResize">
        <ResizeSVG />
      </div>
    </div>
  </div>

  <div :class="joinClasses('container', modifiers)">
    <div class="detail typo-tiny" v-if="activeItem && !hoverActive">
      {{ detail }}
    </div>
    <div class="detail typo-tiny" v-if="hoverActive">
      {{ activeHoveredElement }}
    </div>

    <div class="diagram-control-group">
      <ZoomInSVG @click="diagramZoomIn" class="diagram-control" />
      <ZoomOutSVG @click="diagramZoomOut" class="diagram-control" />
      <ResetSVG @click="diagramResetPan" class="diagram-control" />
    </div>
    <svg
      @mouseover="dragging()"
      @mousedown="grabbing = true"
      @mouseup="grabbing = false"
      :class="{
        'diagram diagram__svg': true,
        'diagram--grab': !resize,
        'diagram--grabbing': !resize && grabbing,
        'diagram--height': definition,
        'diagram--resize': activeItem?.geometry && dragAndResize,
        'diagram--resizing': resizing
      }"
      v-if="elements"
    >
      <defs>
        <mask :id="maskId">
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="black"
          />
          <circle
            v-if="shape === 'circle'"
            class="shape"
            cx="150"
            cy="150"
            r="149"
            fill="white"
          />
          <template v-else-if="shape === 'circles'">
            <circle
              class="shape"
              cx="78"
              cy="150"
              r="64"
              fill="white"
            />
            <circle
              class="shape"
              cx="225"
              cy="150"
              r="64"
              fill="white"
            />
          </template>
          <rect
            v-else-if="shape === 'rectangle'"
            class="shape"
            x="5"
            y="70"
            width="290"
            height="160"
            fill="white"
          />
          <path
            v-if="shape === 'squircle'"
            d="M66 274c-26-18-46-43-57-73V99c11-30 31-55 57-73h168c26 18 46 43 57 73v102c-11 30-31 55-57 73H66z"
            class="shape"
            fill="white"
            fill-rule="evenodd"
          />
        </mask>
      </defs>
      <g :mask="`url(#${maskId})`">
        <rect
          class="background"
          x="0"
          y="0"
          :width="maxWidth"
          :height="maxHeight"
        />
        <template v-for="type in DEFAULT_CIRCLE_TYPES" :key="type">
          <g
            v-for="element in elements[type]"
            :key="element.id"
            :id="activeItem?.id === element.id && !resize ? 'element--active-inner' : null"
          >
            <circle
              :class="`element ${type} ${activeHoverClass(
                activeItem,
                element.id
              )}`"
              v-bind="offsetCircle(element.geometry, type)"
              @mouseover="onElementHover(element)"
              @mouseleave="hoverActive = false"
              @mousedown="grabbing = true"
              @mouseup="grabbing = false"
            />
          </g>
        </template>
        <template v-for="type in DEFAULT_RECT_TYPES" :key="type">
          <g
            v-for="element in elements[type]"
            :key="element.id"
          >
            <rect
              :class="`element ${type} ${activeHoverClass(
                activeItem,
                element.id
              )}`"
              :id="activeItem?.id === element.id ? 'element--active-inner' : null"
              v-bind="offsetRect(element.geometry)"
              @mouseover="onElementHover(element)"
              @mouseleave="hoverActive = false"
              @mousedown="grabbing = true"
              @mouseup="grabbing = false"
              @dblclick="startResize"
            />
          </g>
        </template>
        <use xlink:href="#element--active-inner" />
      </g>
      <g
        v-for="sidewall in elements.sidewalls"
        :key="sidewall.id"
        :class="`sidewall-group ${activeHoverClass(activeItem, sidewall.id)}`"
        :id="activeItem?.id === sidewall.id ? 'element--active-outer' : null"
        @mouseover="onElementHover(sidewall)"
        @mouseleave="hoverActive = false"
        @mousedown="grabbing = true"
        @mouseup="grabbing = false"
      >
        <rect
          class="element sidewall"
          v-bind="offsetRect(sidewall.geometry)"
        />
        <text
          dominant-baseline="middle"
          text-anchor="middle"
          class="element-text sidewall-text"
        >
          {{ sidewall.symbol }}
        </text>
      </g>
      <use xlink:href="#element--active-outer" />
      <template v-if="activeItem?.geometry && dragAndResize">
        <rect
          v-for="(handle, index) in resizeHandleList"
          :class="handle.class"
          :key="index"
          :width="handleSize"
          :height="handleSize"
          stroke-width="1"
          stroke="white"
          fill="black"
        />
      </template>
    </svg>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { SVG } from '@svgdotjs/svg.js';
import interact from 'interactjs';
import '@svgdotjs/svg.draggable.js';
import '@svgdotjs/svg.panzoom.js';
import { v4 as uuid } from 'uuid';
import I18n from 'i18n-js';
import joinClasses from '@/functions/join_classes';

import Hand32Svg from '@/images/hand-32.svg';
import ResizeSVG from '@/images/define-bounds.svg';
import ZoomInSVG from '@/images/zoom-in.svg';
import ZoomOutSVG from '@/images/zoom-out.svg';
import ResetSVG from '@/images/reset.svg';

const DEFAULT_RECT_TYPES = [
  'buntons',
  'wall_brackets',
  'guides',
  'pipe_dividers',
  'brattice_walls',
  'skips_and_cages',
];

const DEFAULT_CIRCLE_TYPES = ['pipes', 'cables'];

const props = defineProps({
  shape: { type: String, default: '' },
  elements: { type: Object, default: null },
  activeItem: { type: Object, default: null },
  modifiers: { type: Array, default: () => [] },
  definition: { type: Boolean, default: false },
  minMax: { type: Object, required: false, default: null },
});
const emit = defineEmits(['updateActiveElement']);
const hoverActive = ref(false);
const activeHoveredElement = ref(null);
const maskId = uuid();
const grabbing = ref(false);
const maxWidth = ref(600);
const maxHeight = ref(1000);
const draw = ref();
const activeDraggableElement = ref();
const sidewallGroup = ref();
const circleViewbox =
  props.shape === 'circle' || props.shape === 'squircle'
    ? '-25 -25 350 350'
    : '-25 0 350 350';
const viewBoxValues = props.definition ? '-25 -25 350 350' : circleViewbox;
const zoomLevel = 0.1;
const handleMaximumSize = ref(3);
const handleSize = ref(0);
const resize = ref(false);
const resizing = ref(false)
const reduceHandleSize = 1.5;
const resizableElement = ref(null);
const resizeHandleList = ref([
  { class: 'top left element--resize-top-left' },
  { class: 'top element--resize-top' },
  { class: 'top right element--resize-top-right' },
  { class: 'left element--resize-left' },
  { class: 'right element--resize-right' },
  { class: 'bottom left element--resize-bottom-left' },
  { class: 'bottom element--resize-bottom' },
  { class: 'bottom right element--resize-bottom-right' },
]);
let minZoomLevel = '';
let canGrab = false;

onMounted(() => {
  updateNewElementGeometry();

  draw.value = SVG('.diagram__svg');
  draw.value
    .width(maxWidth.value)
    .height(maxHeight.value)
    .viewbox(viewBoxValues)
    .panZoom({ zoomMin: draw.value.zoom(), panning: true, pinchZoom: false });

  draw.value.on('panning', (event) => {
    if (resizing.value) event.preventDefault()
  })

  minZoomLevel = draw.value.zoom();

  activeDraggableElement.value = SVG('.element--active');

  initializeSidewall();

  resizeInit();

  resizeUpdate();
});

function updateNewElementGeometry() {
  if (props.activeItem?.id && props.activeItem.id !== 'new' || !props.activeItem?.element_type) return;
  let xValue = null;
  let yValue = null;
  let widthValue = null;
  let heightValue = null;
  let sizeValue = null;
  let pipesAndCables = false;

  switch (props.activeItem.element_type) {
    case 'pipes':
    case 'cables':
      sizeValue = 5;
      pipesAndCables = !pipesAndCables;
      break;
    default:
      widthValue = 50;
      heightValue = 50;
      break;
  }

  switch (props.shape) {
    case 'circles':
      xValue = pipesAndCables ? 75 : 55;
      yValue = pipesAndCables ? 145 : 125;
      break;
    default:
      xValue = pipesAndCables ? 145 : 120;
      yValue = pipesAndCables ? 140 : 120;
      break;
  }

  props.activeItem.geometry = {
    x: xValue,
    y: yValue,
    width: widthValue,
    height: heightValue,
    size: sizeValue
  };
  emit('updateActiveElement', props.activeItem.geometry);
}

/**
 * Sets a boolean to hide or show the drag and resize icons
 */
const dragAndResize = computed(() => {
  return props.activeItem?.element_type !== 'pipes' &&
    props.activeItem?.element_type !== 'cables';
});

/**
 * Gets the name and set number
 */
const detail = computed(() => {
  const startAndEndSet =
    props.activeItem?.start_set || props.activeItem?.end_set
      ? `${props.activeItem.start_set} - ${props.activeItem.end_set}`
      : '';
  const set = props.activeItem?.set_number
    ? props.activeItem.set_number
    : startAndEndSet;
  const nameAndSet = props.activeItem?.name
    ? `${props.activeItem.name}, ${I18n.t('shafts.condition.set')} ${set}`
    : '';
  return nameAndSet;
});

/**
 * Fix the handle and text position
 * when inputs are used instead of the
 * grab and resize functionality
 */
watch(
  () => [
      props.activeItem?.geometry?.x,
      props.activeItem?.geometry?.y,
      props.activeItem?.geometry?.width,
      props.activeItem?.geometry?.height
    ],
  () => {
    updateHandleAndTextPosition();
  }
);

function initializeSidewall() {
  sidewallGroup.value = draw.value.find('.sidewall-group');
  if (!sidewallGroup.value) return;

  sidewallGroup.value?.forEach((sidewall) => {
    /**
     * set sidewall text to the center of the rect element
     */
    setSidewallTextPosition(sidewall.node.firstChild, sidewall.node.children[1]);
  });
}

function setSidewallTextPosition(sidewallElement, sidewallText) {
  /**
   * get sidewall rect values and update the sidewall text position
   */
  if (!sidewallText) return;

  const rectWidth = parseFloat(
    sidewallElement?.getAttribute('width')
  );
  const rectHeight = parseFloat(
    sidewallElement?.getAttribute('height')
  );
  const rectX = parseFloat(sidewallElement?.getAttribute('x'));
  const rectY = parseFloat(sidewallElement?.getAttribute('y'));
  const textX = rectWidth * 0.5 + rectX;
  const textY = rectHeight / 2 + rectY;

  sidewallText.setAttribute('x', textX);
  sidewallText.setAttribute('y', textY);
}

function resizeInit() {
  interact('.diagram--resize')
    .resizable({
      edges: {
        left: '.left',
        right: '.right',
        bottom: '.bottom',
        top: '.top'
      }
    })
    .on('resizemove', function(event) {
      resizeUpdate(event);
    })
    .on('resizestart', function() {
      resizing.value = true
    })
    .on('resizeend', function() {
      resizing.value = false
    })
}

function resizeUpdate(target) {
  /**
   * Resize the rect, not the group, it will resize automatically
   */
  if (!resizableElement.value) return;

  for (const attr of ['width', 'height', 'top', 'left']) {
    const otherAttr = attr === 'top' || attr === 'left' ? (attr === 'left' ? 'x' : 'y') : attr;

    let attributes = Number(resizableElement.value?.getAttribute(otherAttr));
    if (target) {
      attributes += target.deltaRect[attr] / 2;
      resizableElement.value.setAttribute(otherAttr, convertToPositiveValue(attributes));
    }
  }

  emit('updateActiveElement', {
    x: Math.round(resizableElement.value.getAttribute('x')),
    y: Math.round(resizableElement.value.getAttribute('y')),
    width: Math.round(resizableElement.value.getAttribute('width')),
    height: Math.round(resizableElement.value.getAttribute('height')),
  });

  target && setHandleLocations(resizableElement.value);
}

function dragging() {
  /**
   * canGrab makes sure the initializing is done once
   */
  activeDraggableElement.value = SVG('.element--active');
  if (!activeDraggableElement.value || canGrab) return;
  canGrab = true;
  activeDraggableElement.value.draggable(!resize.value);
  dragActiveElement(activeDraggableElement.value);

  activeDraggableElement.value?.on('dragmove', function () {
    dragActiveElement(activeDraggableElement.value);
  });
}

function dragActiveElement(activeDraggableElement) {
  let activeElementX = 0;
  let activeElementY = 0;
  let activeElementWidth = 0;
  let activeElementHeight = 0;
  let activeElementSize = null;
  const draggableElement = activeDraggableElement.type === 'g' ? activeDraggableElement?.findOne('rect') : activeDraggableElement;

  /**
   * This helps get the right values from the active element,
   * in the weird case of sidewalls where the active element would be the group
   * we have to extract the values from the group's child rect element and update the
   * child text element so that it also shows up in the right position (center of the sidewall),
   * having the 'element--active' class set on the group also helps when grabbing to update
   * the position, without that the text child disturbs the grabbing process as it sits
   * on top of the rect child element...
   */
  switch (activeDraggableElement.type) {
    case 'g':
      activeElementX = Math.round(parseFloat(draggableElement.node.getAttribute('x')));
      activeElementY = Math.round(parseFloat(draggableElement.node.getAttribute('y')));
      activeElementWidth =
        Math.round(parseFloat(draggableElement.node.getAttribute('width'))) ||
        props.activeItem?.geometry?.width;
      activeElementHeight =
        Math.round(parseFloat(draggableElement.node.getAttribute('height'))) ||
        props.activeItem?.geometry?.height;
      break;

    case 'circle':
      activeElementX = Math.round(
        parseFloat(draggableElement.node.getAttribute('cx')) -
          parseFloat(draggableElement.node.getAttribute('r'))
      );
      activeElementY = Math.round(
        parseFloat(draggableElement.node.getAttribute('cy')) -
          parseFloat(draggableElement.node.getAttribute('r'))
      );
      activeElementWidth = null;
      activeElementHeight = null;
      activeElementSize = props.activeItem?.geometry?.size;
      break;

    default:
      activeElementX = Math.round(parseFloat(draggableElement.node.getAttribute('x')));
      activeElementY = Math.round(parseFloat(draggableElement.node.getAttribute('y')));
      activeElementWidth =
        Math.round(parseFloat(draggableElement.node.getAttribute('width'))) ||
        props.activeItem?.geometry?.width;
      activeElementHeight =
        Math.round(parseFloat(draggableElement.node.getAttribute('height'))) ||
        props.activeItem?.geometry?.height;
      break;
  }

  /**
   * emits the active element updated values to the form component
   * which helps keep everything in-sync and for saving to the database
   */
  let xValue;
  let yValue;

  xValue = activeElementX >= props.minMax.min ? activeElementX : props.minMax.min;
  xValue = activeElementX <= props.minMax.max ? xValue : props.minMax.max;

  yValue = activeElementY >= props.minMax.min ? activeElementY : props.minMax.min;
  yValue = activeElementY <= props.minMax.max ? yValue : props.minMax.max;

  emit('updateActiveElement', {
    x: xValue,
    y: yValue,
    width: activeElementWidth,
    height: activeElementHeight,
    size: activeElementSize,
  });
}

function diagramZoomIn() {
  const newZoomLevel = draw.value.zoom() + zoomLevel;
  draw.value.zoom(newZoomLevel);
}

function diagramZoomOut() {
  const stringMinZoomLevel = minZoomLevel.toString();
  const stringCurrentZoomLevel = draw.value.zoom().toString();
  const newMinZoomLevel = stringMinZoomLevel.substring(
    0,
    stringMinZoomLevel.length - 1
  );
  const newCurrentZoomLevel = stringCurrentZoomLevel.substring(
    0,
    stringCurrentZoomLevel.length - 1
  );
  if (
    minZoomLevel < draw.value.zoom() &&
    newMinZoomLevel !== newCurrentZoomLevel
  ) {
    const newZoomLevel = draw.value.zoom() - zoomLevel;
    draw.value.zoom(newZoomLevel);
  } else {
    diagramResetPan();
  }
}

function diagramResetPan() {
  draw.value.viewbox(viewBoxValues);
}

function onElementHover(element) {
  hoverActive.value = true;
  activeHoveredElement.value = element.name;
}

function offsetRect({ x, y, width, height }) {
  return (
    x && y && width && height && {
      x: x,
      y: y,
      width: width,
      height: height,
    }
  );
}

function offsetCircle({ x, y, size }, type) {
  const radius = type === 'cables' ? (size / 2) * 0.75 : size / 2;
  return (
    x && y && size && {
      cx: parseFloat(x) + radius,
      cy: parseFloat(y) + radius,
      r: radius,
    }
  );
}

function activeHoverClass(activeItem, current) {
  if (activeItem?.id && activeItem.id === current) {
    return 'element--active';
  } else {
    return '';
  }
}

/**
 * This function takes the target element and positions
 * the handles accordingly
 */
const setHandleLocations = (target) => {
  const activeSidewallText = activeDraggableElement.value?.node?.children[1];
  let resizeHandleGroup = getResizeHandles(target);

  resizeHandleGroup.forEach(handle => {
    handle.element.node.setAttribute('x', handle.x.toString());
    handle.element.node.setAttribute('y', handle.y.toString());
  });

  setSidewallTextPosition(target, activeSidewallText);
};

/**
 * updates the handle and text position
 * when inputs are used instead of the
 * grab and resize functionality
 */
function updateHandleAndTextPosition() {
  setTimeout(() => {
    if (resize.value) {
      const currentActiveElement = getActiveElement();
      currentActiveElement && setHandleLocations(currentActiveElement);
    }

    if (!activeDraggableElement.value) return;
    const sidewallElement = activeDraggableElement.value?.node?.children[0];
    const sidewallText = activeDraggableElement.value?.node?.children[1];
    setSidewallTextPosition(sidewallElement, sidewallText);
  }, 120);
}

function getResizeHandles(target) {
  if (!target) return;

  const handleOffSet = handleMaximumSize.value - reduceHandleSize;
  let xValue = convertToPositiveValue(Math.round(Number(target.getAttribute('x'))));
  let yValue = convertToPositiveValue(Math.round(Number(target.getAttribute('y'))));
  let widthValue = convertToPositiveValue(Math.round(Number(target.getAttribute('width'))));
  let heightValue = convertToPositiveValue(Math.round(Number(target.getAttribute('height'))));

  handleSize.value = handleMaximumSize.value;
  return [
    {
      element: draw.value.findOne('.element--resize-top-left'),
      x: xValue - handleOffSet,
      y: yValue - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-top'),
      x: xValue + widthValue / 2 - handleOffSet,
      y: yValue - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-top-right'),
      x: xValue + widthValue - handleOffSet,
      y: yValue - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-left'),
      x: xValue - handleOffSet,
      y: yValue + heightValue / 2 - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-right'),
      x: xValue + widthValue - handleOffSet,
      y: yValue + heightValue / 2 - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-bottom-left'),
      x: xValue - handleOffSet,
      y: yValue + heightValue - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-bottom'),
      x: xValue + widthValue / 2 - handleOffSet,
      y: yValue + heightValue - handleOffSet
    },
    {
      element: draw.value.findOne('.element--resize-bottom-right'),
      x: xValue + widthValue - handleOffSet,
      y: yValue + heightValue - handleOffSet
    },
  ];
}

function startResize() {
  const currentActiveElement = getActiveElement();

  if (!currentActiveElement) return;
  resize.value = !resize.value;

  if (resize.value) {
    activeDraggableElement.value.draggable(false);

    currentActiveElement.classList.add('resizable');
    resizableElement.value = currentActiveElement;

    /**
     * set handles
     */
    setHandleLocations(resizableElement.value);
  } else {
    activeDraggableElement.value.draggable(true);

    currentActiveElement.classList.remove('resizable');

    /**
     * hide handles when resize is disabled
     */
    handleSize.value = 0;
  }
}

function getActiveElement() {
  activeDraggableElement.value = SVG('.element--active');
  if (!props.activeItem || !activeDraggableElement.value) return;
  const isSidewall = activeDraggableElement.value.node.classList.contains('sidewall-group');
  return isSidewall ? activeDraggableElement.value.node.firstChild : activeDraggableElement.value.node;
}

function convertToPositiveValue(value) {
  return Math.sign(value) === 1 ? value : 0;
}
</script>

<style scoped lang='scss'>
@import '@/stylesheets/global';

$background: map-get($background-color, lighter);

.container {
  background-color: rgb(40 39 40 / 41%);
  background-size: bu(2) bu(2);
  background-position: 50% 50%;
  background-image: repeating-linear-gradient(
      $background,
      $background 1px,
      transparent 1px,
      transparent 100%
    ),
    repeating-linear-gradient(
      90deg,
      $background,
      $background 1px,
      transparent 1px,
      transparent 100%
    );
  border-radius: map-get($border-radius, heavier);
  height: 100%;
  position: relative;

  @include breakpoint(large) {
    border-radius: map-get($border-radius, mega-heavy);
    background-size: bu(3) bu(3);
  }

  &--small {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .diagram {
      max-height: rem(550);
    }
  }
}
.background {
  fill: map-get($background-color, lightest);
}
.diagram {
  width: 100%;
  height: auto;
  border-radius: map-get($border-radius, heavier);

  &--grab {
    cursor: grab;
  }

  &--grabbing {
    cursor: grabbing;
  }

  &--height {
    height: 100%;
  }
}
.shape {
  stroke: none;
}
.element {
  fill: rgb(255 255 255 / 10%);
  stroke: rgb(255 255 255 / 40%);
  stroke-width: 1;
  &:hover {
    fill: rgb(255 255 255 / 70%);
  }
}

.element--active {
  stroke: map-get(map-get($region-color, structure), light);
  fill: map-get(map-get($region-color, structure), dark);

  &.resizable,
  & > .resizable {
    stroke-dasharray: 2;
    stroke-width: 1;
  }
}

.sidewall-group {
  &:hover {
    .element {
      fill: rgb(255 255 255 / 70%);
    }
  }

  .sidewall-text {
    fill: rgb(255 255 255 / 13%);
    font-size: bu(1.5);
    letter-spacing: bu(.2);
    font-weight: 600;
    fill: map-get($background-color, lighter);
  }

  .sidewall {
    stroke: none;
    fill: map-get($background-color, default);
  }

  &.element--active .sidewall {
    stroke: map-get(map-get($region-color, structure), light);
    fill: map-get(map-get($region-color, structure), dark);
    &:hover {
      fill: rgb(255 255 255 / 70%);
    }
  }
}

.detail {
  color: $white;
  font-weight: 700;
  height: auto;
  padding: bu(0.5) bu(1);
  position: absolute;
  top: bu(1.5);
  text-align: center;
  left: 0;
  right: 0;
}

.diagram-control-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: bu(2);
  right: bu(2);
  box-shadow: map-get($box-shadow, popover);
  background-color: map-get($background-color, default);
  border-radius: map-get($border-radius, heavy);
  cursor: pointer;
  user-select: none;

  .diagram-control {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: bu(1.5);
    user-select: none;

    &:nth-child(1) {
      cursor: zoom-in;
    }

    &:nth-child(2) {
      cursor: zoom-out;
    }
  }
}

.pan-resize-control-group {
  display: flex;
  margin-bottom: bu(3);
  justify-content: center;

  &__inner {
    display: flex;
    border-radius: bu(1);
    padding: bu(.5);
    box-shadow: map-get($box-shadow, popover);
  }

  .button {
    border: bu(.3) solid map-get($foreground-color, darkest);

    &--active {
      border: bu(.3) solid map-get($foreground-color, light);
      background-color: map-get($background-color, dark);
    }
  }
}
</style>
